import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FEE_CHECK_FIELDS = [
  ["total_portfolio", "Portfolio"],
  ["num_accounts", "Accounts"],
  ["investment_fees", "Investment fees"],
  ["platform_fees", "Platform fees"],
  ["adviser_fees", "Adviser fees"],
];

const openFile = async (kind, itemId, file) => {
  const isImage = file.content_type?.startsWith("image/") && !file.content_type.includes("hei");
  const tab = isImage ? window.open("", "_blank") : null;
  try {
    const { data } = await axios.get(`${API}/${kind}/${itemId}/files/${file.id}`, {
      withCredentials: true,
      responseType: "blob",
    });
    const url = URL.createObjectURL(data);
    if (tab) {
      tab.location.href = url;
    } else {
      const a = document.createElement("a");
      a.href = url;
      a.download = file.original_filename;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch {
    tab?.close();
    toast.error("Couldn't open that file.");
  }
};

export const FileButtons = ({ kind, item }) =>
  item.files?.length > 0 && (
    <div className="mt-5 flex flex-wrap gap-3" data-testid={`${kind}-files-${item.id}`}>
      {item.files.filter((f) => !f.is_deleted).map((f) => (
        <button
          key={f.id}
          data-testid={`open-file-${f.id}`}
          onClick={() => openFile(kind, item.id, f)}
          className="inline-flex items-center gap-2 border border-[#0A2333]/30 px-4 py-2 text-xs text-[#0A2333] transition-colors duration-300 hover:bg-[#0A2333] hover:text-[#F9F8F4]"
        >
          <FileText size={14} />
          {f.original_filename}
        </button>
      ))}
    </div>
  );

const SubmissionCard = ({ kind, fields, c, fmtDate }) => (
  <article data-testid={`${kind}-card-${c.id}`} className="border border-black/10 bg-white px-6 md:px-8 py-6">
    <div className="flex flex-wrap items-start justify-between gap-4">
      <h3 className="font-serif text-2xl tracking-tight text-[#111413]">{c.name}</h3>
      <p className="text-xs text-[#4A524D]">{fmtDate(c.created_at)}</p>
    </div>
    <div className="mt-4 flex flex-wrap gap-x-8 gap-y-1 text-sm text-[#4A524D]">
      <a href={`mailto:${c.email}`} className="hover:text-[#0A2333]">{c.email}</a>
      <a href={`tel:${c.phone}`} className="hover:text-[#0A2333]">{c.phone}</a>
    </div>
    <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-[11px] uppercase tracking-[0.15em] text-[#A78558]">
      {fields.filter(([k]) => c[k]).map(([k, label]) => (
        <span key={k}>{label}: {c[k]}</span>
      ))}
    </div>
    {c.message && (
      <p className="mt-4 text-sm leading-relaxed text-[#111413] border-l-2 border-[#0A2333]/30 pl-4 max-w-3xl">{c.message}</p>
    )}
    <FileButtons kind={kind} item={c} />
  </article>
);

export const AdminSubmissions = ({ kind, title, empty, fields = [], fmtDate }) => {
  const [items, setItems] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/${kind}`, { withCredentials: true })
      .then(({ data }) => setItems(kind === "client-uploads" ? data.filter((d) => d.files?.length) : data))
      .catch(() => setItems([]));
  }, [kind]);

  return (
    <section className="mt-10" data-testid={`admin-${kind}`}>
      {title && <h2 className="font-serif text-3xl tracking-tight text-[#111413]">{title}</h2>}
      {items === null ? (
        <p className="mt-6 text-sm text-[#4A524D]">Loading…</p>
      ) : items.length === 0 ? (
        <p className="mt-6 text-sm text-[#4A524D]" data-testid={`admin-${kind}-empty`}>{empty}</p>
      ) : (
        <div className="mt-8 space-y-6">
          {items.map((c) => (
            <SubmissionCard key={c.id} kind={kind} fields={fields} c={c} fmtDate={fmtDate} />
          ))}
        </div>
      )}
    </section>
  );
};
